/**
 * EditableKinkRow Component
 * 
 * A table row with inline editing capability for kink properties.
 * Features:
 * - Inline editable numeric fields (Level, Realism, Ethics, Rarity, Dom, Sub)
 * - "Related" button to load associated kinks on demand
 * - Premium visual styling with score badges
 * Scale: 1-100 for all numeric fields.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link2, Unlink, Sparkles, Loader2, ChevronDown } from 'lucide-react';
import api from '../services/api';
import { to5Scale, to10Scale, render5Scale } from '../utils/displayConverters.jsx';
import { Spinner, SlideUp, Float, Sparkle, SpinSlow } from './Animations';

function EditableKinkRow({ kink, categories, onUpdate, onDelete, rowIndex = 0, isDevMode = false }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Related kinks state (replaces parent-child hierarchy)
    const [relatedKinks, setRelatedKinks] = useState([]);
    const [loadingRelated, setLoadingRelated] = useState(false);
    const [showRelated, setShowRelated] = useState(false);

    // Track which field is being edited
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef(null);

    // Focus input when editing starts
    useEffect(() => {
        if (editingField && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingField]);

    // Load related kinks on demand
    const loadRelatedKinks = async () => {
        if (relatedKinks.length > 0) {
            setShowRelated(!showRelated);
            return;
        }

        setLoadingRelated(true);
        try {
            const data = await api.getKink(kink.id);
            // Combine childKinks and relatedKinks
            const related = [
                ...(data.childKinks || []).map(k => ({ ...k, relation: 'child' })),
                ...(data.relatedKinks || []).map(k => ({ ...k, relation: 'related' }))
            ];
            setRelatedKinks(related);
            setShowRelated(true);
        } catch (error) {
            console.error('Failed to load related kinks:', error);
        } finally {
            setLoadingRelated(false);
        }
    };

    // Start editing a field
    const startEditing = (field, currentValue) => {
        setEditingField(field);
        setEditValue(currentValue?.toString() || '');
    };

    // Save field change
    const saveField = async (field) => {
        if (editValue === kink[field]?.toString()) {
            setEditingField(null);
            return;
        }

        setSaving(true);
        try {
            let value = editValue;
            // Convert to number for numeric fields
            if (['weirdnessScore', 'realism', 'ethics', 'rarity', 'domAppeal', 'subAppeal', 'socialAcceptability', 'physicalAppeal'].includes(field)) {
                value = Math.min(100, Math.max(1, parseInt(editValue) || 1));
            }

            const updated = await api.updateKink(kink.id, { [field]: value });
            onUpdate(updated);
            setEditingField(null);
        } catch (error) {
            console.error('Failed to save:', error);
        } finally {
            setSaving(false);
        }
    };

    // Handle key press in input
    const handleKeyDown = (e, field) => {
        if (e.key === 'Enter') {
            saveField(field);
        } else if (e.key === 'Escape') {
            setEditingField(null);
        }
    };

    // Delete kink
    const handleDelete = async () => {
        setDeleting(true);
        try {
            await api.deleteKink(kink.id);
            onDelete(kink.id);
        } catch (error) {
            console.error('Failed to delete:', error);
            alert('Failed to delete: ' + error.message);
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // Score color styling - updated for wine theme
    const getScoreStyle = (score, type = 'level') => {
        if (score == null) return { color: '#666' };

        // Normalize to 0-1
        let intensity;
        if (type === 'ethics' || type === 'realism' || type === 'social') {
            // High = good (white), Low = bad (wine)
            intensity = 1 - score / 100;
        } else {
            // High = extreme (wine), Low = neutral (white)
            intensity = score / 100;
        }

        // Gradient from white (#fff) to wine (#C73E52)
        const r = Math.round(255 - intensity * 56);
        const g = Math.round(255 - intensity * 204);
        const b = Math.round(255 - intensity * 173);

        return {
            color: `rgb(${r}, ${g}, ${b})`,
            textShadow: intensity > 0.5 ? '0 0 8px rgba(199, 62, 82, 0.3)' : 'none'
        };
    };

    // Render an editable number field with normalized display value
    const renderNumberField = (field, value, type = 'level', displayScale = '1-100') => {
        // Determine display value based on scale
        let displayValue = value;
        if (displayScale === '1-5') {
            displayValue = to5Scale(value);
        } else if (displayScale === '1-10') {
            displayValue = to10Scale(value);
        }

        if (editingField === field) {
            return (
                <input
                    ref={inputRef}
                    type="number"
                    min="1"
                    max="100"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => saveField(field)}
                    onKeyDown={(e) => handleKeyDown(e, field)}
                    className="w-12 px-1 py-0.5 bg-dark-700 border border-wine-500/50 rounded text-center text-sm text-dark-100 focus:outline-none focus:ring-1 focus:ring-wine-500"
                    disabled={saving}
                />
            );
        }

        // If it's a 1-5 scale, we render it as dots
        if (displayScale === '1-5') {
            return (
                <button
                    onClick={() => startEditing(field, value)}
                    className="px-2 py-1 rounded-md bg-dark-800/40 hover:bg-dark-700/60 transition-all flex items-center justify-center outline-none focus:ring-1 focus:ring-wine-500/30"
                    title={`Click to edit ${field === 'weirdnessScore' ? 'Kinkiness' : field} (DB: ${value})`}
                >
                    {render5Scale(displayValue)}
                </button>
            );
        }

        return (
            <button
                onClick={() => startEditing(field, value)}
                className="w-10 h-6 rounded-md bg-dark-800/60 hover:bg-dark-700/80 hover:ring-2 hover:ring-wine-500/40 transition-all font-mono text-sm font-medium flex items-center justify-center"
                style={getScoreStyle(value, type)}
                title={`Click to edit ${field === 'weirdnessScore' ? 'Kinkiness' : field} (DB: ${value})`}
            >
                {displayValue ?? '-'}
            </button>
        );
    };

    // Render an editable text field
    const renderTextField = (field, value, placeholder = '-') => {
        if (editingField === field) {
            return (
                <input
                    ref={inputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => saveField(field)}
                    onKeyDown={(e) => handleKeyDown(e, field)}
                    className="w-full px-2 py-0.5 bg-dark-700 border border-wine-500/50 rounded text-sm text-dark-100 focus:outline-none focus:ring-1 focus:ring-wine-500"
                    disabled={saving}
                />
            );
        }
        return (
            <button
                onClick={() => startEditing(field, value)}
                className="text-left text-dark-400 hover:text-dark-200 text-sm truncate block w-full transition-colors"
                title={value || placeholder}
            >
                {value || placeholder}
            </button>
        );
    };

    // Row alternation for visual hierarchy
    const isEvenRow = rowIndex % 2 === 0;

    return (
        <>
            <div className={`grid gap-2 py-2.5 px-4 transition-colors group items-center ${isDevMode
                ? 'grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px]'
                : 'grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px]'
                } ${isEvenRow ? 'bg-dark-900/15' : 'bg-dark-900/45'
                } hover:bg-dark-800/60`}>
                {/* Name + Summary */}
                <div className="flex items-start gap-2 min-w-0">
                    {/* Related button - only show if kink has related items */}
                    {(kink.relatedCount > 0 || showRelated || relatedKinks.length > 0) ? (
                        <button
                            onClick={loadRelatedKinks}
                            className={`flex-shrink-0 h-7 px-2 rounded flex items-center justify-center gap-1 transition-all ${showRelated
                                ? 'bg-wine-500/30 text-wine-400 ring-2 ring-wine-500/40'
                                : 'bg-wine-600/20 text-wine-300 hover:bg-wine-600/30 ring-1 ring-wine-500/30'
                                }`}
                            title={showRelated ? 'Hide related kinks' : `Show ${kink.relatedCount || relatedKinks.length} related kinks`}
                        >
                            {loadingRelated ? (
                                <Spinner size="xs" color="wine" />
                            ) : showRelated ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                            ) : (
                                <>
                                    <Link2 className="w-3.5 h-3.5" />
                                    <span className="text-xs font-medium">{kink.relatedCount || relatedKinks.length}</span>
                                </>
                            )}
                        </button>
                    ) : (
                        <div className="w-7 flex-shrink-0" /> /* Spacer when no relations */
                    )}

                    <div className="min-w-0 flex-1">
                        <a
                            href={`/kink/${kink.id}`}
                            className="text-dark-100 hover:text-wine-400 font-bold transition-colors truncate block text-sm"
                        >
                            {kink.name}
                        </a>
                        {(kink.summary || kink.description) && (
                            <p className="text-dark-500 text-xs mt-0.5 line-clamp-1 leading-relaxed">
                                {kink.summary || kink.description?.split('.')[0]}
                            </p>
                        )}
                    </div>
                </div>

                {/* Category - editable */}
                <div className="truncate">
                    {renderTextField('category', kink.category, '-')}
                </div>

                {/* Kinkiness Level - Display as 1-5 */}
                <div className="flex justify-center">
                    {renderNumberField('weirdnessScore', kink.weirdnessScore, 'level', '1-5')}
                </div>

                {/* Realism - Removed from view, keeping as stat */}
                {/* Ethics - Removed from view, keeping as stat */}

                {/* Rarity - Display as 1-10 */}
                <div className="flex justify-center">
                    {renderNumberField('rarity', kink.rarity, 'rarity', '1-5')}
                </div>

                {/* Ethics - Display as 1-5 */}
                <div className="flex justify-center">
                    {renderNumberField('ethics', kink.ethics, 'ethics', '1-5')}
                </div>

                {/* Acceptability - Display as 1-10 */}
                <div className="flex justify-center">
                    {renderNumberField('socialAcceptability', kink.socialAcceptability, 'social', '1-5')}
                </div>



                {/* Tools: Dev Status + Actions */}
                <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Dev Status Indicator */}
                    {isDevMode && (
                        kink.isProcessed ? (
                            <span className="text-green-500 text-xs select-none" title="Processed">✓</span>
                        ) : (
                            <span className="text-dark-700 text-xs select-none" title="Not processed">○</span>
                        )
                    )}

                    {/* Delete Action */}
                    {showDeleteConfirm ? (
                        <div className="flex gap-1">
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="w-5 h-5 bg-red-600/30 text-red-400 rounded text-[10px] hover:bg-red-600/40 flex items-center justify-center"
                            >
                                {deleting ? '…' : '✓'}
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="w-5 h-5 text-dark-400 text-[10px] flex items-center justify-center hover:text-dark-200"
                            >
                                ✕
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="text-dark-600 hover:text-red-400 transition-colors"
                            title="Delete"
                        >
                            🗑
                        </button>
                    )}
                </div>
            </div>

            {/* Related kinks section */}
            {showRelated && relatedKinks.length > 0 && (
                <div className="bg-dark-900/60 border-l-2 border-primary-500/30 ml-4">
                    {relatedKinks.map((related) => (
                        <div
                            key={related.id}
                            className="grid grid-cols-[minmax(180px,1fr)_100px_50px_60px_60px_50px_80px_55px_55px_28px_36px] gap-1 py-2 px-4 items-center hover:bg-dark-800/30"
                        >
                            {/* Name */}
                            <div className="flex items-center gap-2 pl-4">
                                <span className="text-dark-600">└</span>
                                <a
                                    href={`/kink/${related.id}`}
                                    className="text-gray-300 hover:text-white transition-colors truncate"
                                >
                                    {related.name}
                                </a>
                            </div>
                            {/* Category */}
                            <div className="text-dark-500 text-xs truncate">{related.category || '-'}</div>
                            {/* Scores - with badge styling for consistency */}
                            <div className="flex justify-center">
                                <span className="w-8 h-5 rounded bg-dark-800/60 flex items-center justify-center text-xs font-mono" style={getScoreStyle(related.weirdnessScore, 'level')}>{related.weirdnessScore || '-'}</span>
                            </div>
                            <div className="flex justify-center">
                                <span className="w-8 h-5 rounded bg-dark-800/60 flex items-center justify-center text-xs font-mono" style={getScoreStyle(related.realism, 'realism')}>{related.realism || '-'}</span>
                            </div>
                            <div className="flex justify-center">
                                <span className="w-8 h-5 rounded bg-dark-800/60 flex items-center justify-center text-xs font-mono" style={getScoreStyle(related.ethics, 'ethics')}>{related.ethics || '-'}</span>
                            </div>
                            <div className="flex justify-center">
                                <span className="w-8 h-5 rounded bg-dark-800/60 flex items-center justify-center text-xs font-mono" style={getScoreStyle(related.rarity, 'rarity')}>{related.rarity || '-'}</span>
                            </div>
                            {/* Ratios */}
                            <div className="text-center text-xs font-mono text-dark-500">
                                {related.domAppeal != null ? `${related.domAppeal}:${related.subAppeal}` : '-'}
                            </div>
                            <div className="text-center text-xs font-mono text-dark-500">
                                {related.physicalAppeal != null ? `${related.physicalAppeal}:${100 - related.physicalAppeal}` : '-'}
                            </div>
                            <div></div>
                            <div></div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty related state */}
            {showRelated && relatedKinks.length === 0 && !loadingRelated && (
                <SlideUp className="bg-dark-900/40 border-l-2 border-wine-500/20 ml-4 py-5 px-8 flex items-center gap-5">
                    <div className="relative">
                        <Float><Unlink className="w-10 h-10 text-wine-500/30" /></Float>
                        <Sparkle className="absolute -top-1 -right-1"><Sparkles className="w-4 h-4 text-wine-400/50" /></Sparkle>
                    </div>
                    <div className="flex flex-col gap-1">
                        <span className="text-dark-300 text-sm font-medium">No related kinks found</span>
                        <button className="text-wine-500/80 text-xs flex items-center gap-1.5 hover:text-wine-400 transition-colors group">
                            <SpinSlow duration={3}><Sparkles className="w-3 h-3" /></SpinSlow>
                            <span>Use AI Discovery to find connections</span>
                        </button>
                    </div>
                </SlideUp>
            )}
        </>
    );
}

export default EditableKinkRow;
